import { useEffect, useReducer } from "react";
import { sendRequest } from "../../../common/http";
import translate from "@/i18n/translate";
import ApiError from "../../common/elements/ApiError";
import TopProgramsTable from "./TopProgramsTable";
import WidgetLoader from "@/features/common/elements/WidgetLoader";

export interface TopProgramsByCatalogProps {
  mappingId: string;
  onComponentLoad: (component: string) => void;
}

export interface ProgramAndPageViews {
  programName: string;
  pageViews: number;
}

interface TopProgramsByCatalogState {
  programAndPageViews: ProgramAndPageViews[] | null;
  apiError: boolean;
  showLoader: boolean;
}
interface TopProgramsByCatalogAction {
  type: string;
  programAndPageViews: ProgramAndPageViews[] | null;
  apiError: boolean;
  showLoader: boolean;
}

const defaultCatalogAction = {
  type: "",
  programAndPageViews: null,
  showLoader: true,
  apiError: false
};

const initialState = {
  apiError: false,
  programAndPageViews: null,
  showLoader: true
};

const reducer = (
  state: TopProgramsByCatalogState,
  action: TopProgramsByCatalogAction
) => {
  switch (action.type) {
    case "setPrograms":
      return {
        ...state,
        programAndPageViews: action.programAndPageViews
      };
    case "setShowLoader":
      return {
        ...state,
        showLoader: false
      };
    case "setApiError":
      return { ...state, apiError: action.apiError };
    default:
      return state;
  }
};
export default function TopProgramsByCatalog({
  mappingId,
  onComponentLoad
}: TopProgramsByCatalogProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const applyResult = (result: ProgramAndPageViews[]) => {
      dispatch({ ...defaultCatalogAction, type: "setShowLoader" });
      dispatch({
        ...defaultCatalogAction,
        type: "setPrograms",
        programAndPageViews: result
      });
      onComponentLoad("topProgramsByCatalog");
    };
    const onError = () => {
      dispatch({ ...defaultCatalogAction, type: "setShowLoader" });
      dispatch({
        ...defaultCatalogAction,
        type: "setApiError",
        apiError: true
      });
      onComponentLoad("topProgramsByCatalog");
    };
    sendRequest(
      { uri: `/cs/program_page_views?mapping_id=${mappingId}`, camelize: true },
      applyResult,
      onError
    );
  }, [mappingId]);
  const showData = () =>
    !state.showLoader &&
    !state.apiError &&
    state.programAndPageViews &&
    state.programAndPageViews.length;
  return (
    <div className="paper-card paper-card-height">
      <div className="header">
        <div className="info">
          <h3 className="title">
            {translate("app.strategy.topProgramsByCatalogHeader")}
          </h3>

          <p className="description">
            {translate("app.strategy.topProgramsByCatalogDescription")}
          </p>
        </div>
      </div>
      <div className="body">
        {state.showLoader && (
          <div className="loader-position">
            <WidgetLoader
              loaderContainerClasses="loader-container"
              loaderClasses="widget-loader"
            />
          </div>
        )}
        {state.apiError && <ApiError />}
        {showData() && state.programAndPageViews ? (
          <div>
            <TopProgramsTable programDetails={state.programAndPageViews} />
          </div>
        ) : (
          ""
        )}
        {!state.showLoader &&
        state.programAndPageViews &&
        state.programAndPageViews.length === 0 ? (
          <NoProgramViews />
        ) : (
          ""
        )}
      </div>
    </div>
  );
}

const NoProgramViews = () => {
  return (
    <div className="no-plans">
      <div className="no-plans-text">
        {translate("app.strategy.topPrograms.noProgramViews")}
      </div>
    </div>
  );
};
